import { css } from '@emotion/react';
import { Box, Flex, Image, Text } from '@theme-ui/components';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import Checkmark from '@app/components/Checkmark';
import ErrorBoundary from '@app/components/ErrorBoundary';
import { RetailerButton } from '@app/components/RetailerButton/RetailerButton';
import { H2 } from '@app/components/typography';
import useAuthentication from '@app/hooks/useAuthentication';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import colors from '@app/styles/theme/colors';
import { rem } from '@app/styles/theme/layout';

import { getVariantLabel } from '@app/helpers/shop';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout_product as DirectusProduct } from '../../__generated__/PricingPage';
import { getUpgradeVariant, isJPHardware } from '../../helpers';
import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant } from '../../hook/__generated__/PricingPageShopProduct';
import useProductVariant from '../../hook/useProductVariant';
import useUpgradePaths from '../../hook/useUpgradePaths';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import AddToCartErrors from '../AddToCartErrors';
import LicenseSelector from '../LicenseSelector';
import ProductVariantInfo from '../ProductVariantInfo';
import LicenseCardsGrid from './LicenseCardsGrid';
import messages from './ProductLayout.messages';
import { LayoutSubtitle } from './styles';

const ProductTopInfo = ({
  title,
  subtitle,
}: {
  title: string | null;
  subtitle: string;
}) => (
  <>
    <AddToCartErrors />
    <Box sx={{ width: '100%' }} mb={5}>
      <H2 as="h1" data-testid="pricing-product-layout-title">
        {title}
      </H2>
      <LayoutSubtitle>{subtitle}</LayoutSubtitle>
    </Box>
  </>
);

const ProductImage = ({
  selectedVariant,
}: {
  selectedVariant: Variant | null;
}) => (
  <Flex
    sx={{
      alignItems: 'start',
    }}
  >
    {selectedVariant?.product?.image?.url &&
      selectedVariant?.product?.image?.label && (
        <Image
          sx={{ maxWidth: '100%' }}
          data-testid="pricing-product-layout-image"
          src={selectedVariant?.product?.image?.url}
          alt={selectedVariant?.product?.image?.label}
        />
      )}
  </Flex>
);

const ProductPurchaseInfo = ({
  selectedVariant,
  showRetailerButton,
}: {
  selectedVariant: Variant | null;
  showRetailerButton: boolean | null;
}) => {
  return (
    <>
      {selectedVariant && <ProductVariantInfo variant={selectedVariant} />}

      <Flex sx={{ justifyContent: 'flex-start' }}>
        <Flex sx={{ flexDirection: 'column', flexGrow: ['1', 'unset'] }}>
          <Box mb={12}>
            <AddToCartButton product={selectedVariant?.product} />
          </Box>

          {showRetailerButton && <RetailerButton />}

          {isJPHardware(selectedVariant?.product) && (
            <Flex py={3}>
              <Checkmark
                color="white"
                css={css`
                  display: flex;
                  flex: 1;
                  min-width: 24px;
                  height: 24px;
                  background-color: ${colors.acidGreen};
                  border-radius: 99999px;
                  margin-right: 12px;
                  &:after {
                    top: 3px;
                    left: 7px;
                  }
                `}
              />
              <Text
                sx={{ fontWeight: '500', fontSize: rem(12), lineHeight: '1.6' }}
              >
                <FormattedMessage {...messages.mediaIntegrtion} />
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

const Layout = ({ children }) => <Box mb={5}>{children}</Box>;

const Wrapper = ({ children }) => (
  <Flex
    sx={{
      width: '100%',
      flexDirection: ['column', 'row'],
    }}
  >
    {children}
  </Flex>
);

const Left = ({ children }) => (
  <Flex
    mb={[5, 0]}
    sx={{
      width: ['100%', '30%'],
    }}
  >
    {children}
  </Flex>
);

const RightWrapper = ({ children }) => (
  <Flex
    sx={{
      width: '100%',
      flexDirection: ['column', 'column', 'row'],
      ml: [0, '30px'],
    }}
  >
    {children}
  </Flex>
);

const Center = ({ children }) => (
  <Box
    mr={[0, '30px']}
    mb={['30px', 0]}
    sx={{
      width: ['100%', '100%', '75%'],
    }}
  >
    {children}
  </Box>
);

const Right = ({ children }) => (
  <Flex
    mb={[4, 0]}
    sx={{
      width: ['100%', '100%', '30%'],
      flexDirection: 'column',
    }}
  >
    {children}
  </Flex>
);

const ConfigurableProductLayout = ({
  product,
  showRetailerButton,
  onSelectVariant,
  variants,
}: {
  product: DirectusProduct;
  showRetailerButton: boolean | null;
  variants: (Variant | null)[] | null;
  onSelectVariant: Dispatch<SetStateAction<Variant | null>>;
}) => {
  const searchQuery = new URLSearchParams(useLocation().search);

  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();

  const { title } = product;
  const subtitle = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'subtitle',
  });

  const { isAuthenticated, loading: isAuthenticatedLoading } =
    useAuthentication();

  const { upgradeVariants } = useUpgradePaths({
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const upgradeVariant = useMemo(
    () => getUpgradeVariant(variants, upgradeVariants),
    [variants, upgradeVariants],
  );

  const preselectedSKU =
    isAuthenticated && upgradeVariant?.product?.sku
      ? upgradeVariant?.product?.sku
      : searchQuery.get('variant');

  const { selectedVariant, setSelectedVariant } = useProductVariant(
    variants,
    preselectedSKU,
  );

  useEffect(() => {
    if (selectedVariant) {
      const price =
        selectedVariant.product?.priceRange.minimumPrice.finalPrice.value;
      const currency =
        variants?.[0]?.product?.priceRange?.minimumPrice?.finalPrice?.currency;

      pushPerpetualEcommerceEvent('view_item', {
        currency: currency || '',
        value: price || 0,
        items: [
          {
            // The parent product SKU and name is expected for item_id and item_name.
            item_id: product?.sku || '',
            item_name: product?.title || '',
            sku_id: selectedVariant?.product?.sku || '',
            sku_name: selectedVariant?.product?.name || '',
            sdbs_id: selectedVariant?.product?.sdbsId || '',
            item_version: getVariantLabel(selectedVariant) || '',
            item_variant: selectedVariant?.product?.name || '',
            price: price || 0,
            quantity: 1,
          },
        ],
      });
    }
  }, [selectedVariant, product, variants]);

  const handleSetSelectedVariant = (variant: Variant | null) => {
    setSelectedVariant(variant);
    onSelectVariant(variant);
  };

  if (isAuthenticatedLoading) return null;

  return (
    <ErrorBoundary>
      <Layout>
        <ProductTopInfo title={title} subtitle={subtitle} />

        <Wrapper>
          <Left>
            <ProductImage selectedVariant={selectedVariant} />
          </Left>

          <RightWrapper>
            <Center>
              {variants && (
                <LicenseSelector
                  variants={variants}
                  setSelectedVariant={handleSetSelectedVariant}
                  selectedVariant={selectedVariant}
                  upgradeVariants={upgradeVariants}
                  isAuthenticated={isAuthenticated}
                >
                  {(props) => <LicenseCardsGrid {...props} />}
                </LicenseSelector>
              )}
            </Center>
            <Right>
              <ProductPurchaseInfo
                selectedVariant={selectedVariant}
                showRetailerButton={showRetailerButton}
              />
            </Right>
          </RightWrapper>
        </Wrapper>
      </Layout>
    </ErrorBoundary>
  );
};

export default ConfigurableProductLayout;
